import {
    FunctionComponent,
    KeyboardEventHandler,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { CalendarContext } from '../../contexts/CalendarContext';
import React from 'react';
import { DayGridCell, DayGridCellProps } from '../DayGridCell';
import { DefaultWeekdayLabel, WeekdayLabelProps } from '../WeekdayLabel';
import styles from './styles.module.scss';
import { useActiveMonth, useDateFormatter } from '../../hooks';
import { DateRange, isDateInRange } from '../../services/DateRange';
import { CalendarSelectContext } from '../../contexts/CalendarSelectContext';
import { useActiveDay } from '../../hooks/useActiveDay';
import { useWeekdayLabels } from '../../hooks/useWeekdayLabels';
import { useFirstDayOfWeekOffset } from '../../hooks/useFirstDayOfWeekOffset';

export type MonthGridProps = {
    WeekdayLabel?: FunctionComponent<WeekdayLabelProps>;
    Day?: FunctionComponent<DayGridCellProps>;
    className?: string;
    onDayMouseEnter?: (day: DateRange) => void;
    onVisibleMonthChanged?: (month: DateRange) => void;
};

export const MonthGrid: FunctionComponent<MonthGridProps> = ({
    WeekdayLabel = DefaultWeekdayLabel,
    Day = DayGridCell,
    className,
    onDayMouseEnter = () => {},
    onVisibleMonthChanged = () => {},
}) => {
    const { locale, timezone, activeDate, setActiveDate } = useContext(CalendarContext);
    const { isDaySelected, isDayDisabled, onDaySelected } = useContext(CalendarSelectContext);
    const month = useActiveMonth();

    const container = useRef<HTMLTableElement>(null);
    const needsToTakeFocus = useRef<boolean>(false);

    const previousDay = useActiveDay(-1);
    const nextDay = useActiveDay(1);

    const aboveDay = useActiveDay(-7);
    const belowDay = useActiveDay(7);

    useEffect(() => {
        onVisibleMonthChanged(month.range);
    }, [onVisibleMonthChanged, month]);

    const weekdayLabels = useWeekdayLabels({ WeekdayLabel });

    const firstDayOffset = useFirstDayOfWeekOffset(month.days[0].from);

    const formatDay = useDateFormatter({ day: 'numeric' });
    const formatDateFull = useDateFormatter({ dateStyle: 'full' });
    const formatDateLong = useDateFormatter({ dateStyle: 'long' });
    const formatDateMedium = useDateFormatter({ dateStyle: 'medium' });
    const formatDateShort = useDateFormatter({ dateStyle: 'short' });

    useEffect(() => {
        if (needsToTakeFocus.current === true) {
            const dateShort = formatDateShort(activeDate);
            const activeButton = container.current?.querySelector(
                `[data-calendar-day="${CSS.escape(dateShort)}"]`
            );
            if (activeButton && activeButton instanceof HTMLElement) {
                activeButton.focus();
                needsToTakeFocus.current = false;
            }
        }
    }, [activeDate, formatDateShort]);

    const tableRows = useMemo((): Array<Array<ReactNode>> => {
        return month.days.reduce(
            (rows, day) => {
                const dateShort = formatDateShort(day.from);
                const isActive = isDateInRange(activeDate, day);
                const isSelected = isDaySelected(day);
                const isDisabled = isDayDisabled(day);
                const onClick = () => {
                    if (isDisabled) {
                        return;
                    }
                    onDaySelected(day);
                };
                const onMouseEnter = () => {
                    onDayMouseEnter(day);
                };
                const renderedDay = (
                    <button
                        tabIndex={isActive ? 0 : -1}
                        className={styles.cellButton}
                        data-calendar-day={dateShort}
                        onClick={onClick}
                        onMouseEnter={onMouseEnter}
                    >
                        {Day({
                            startOfDay: day.from,
                            endOfDay: day.to,
                            day: formatDay(day.from),
                            dateFull: formatDateFull(day.from),
                            dateLong: formatDateLong(day.from),
                            dateMedium: formatDateMedium(day.from),
                            dateShort,
                            isActive,
                            isSelected,
                            isDisabled,
                        })}
                    </button>
                );
                const previousColumns = rows.slice(0, -1);
                const latestColumn = rows[rows.length - 1];
                if (latestColumn.length === 7) {
                    return [...previousColumns, latestColumn, [renderedDay]];
                }
                return [...previousColumns, [...latestColumn, renderedDay]];
            },
            [new Array(firstDayOffset).fill(null)]
        );
    }, [
        firstDayOffset,
        month,
        Day,
        formatDay,
        formatDateFull,
        formatDateLong,
        formatDateMedium,
        formatDateShort,
        activeDate,
        isDaySelected,
        onDaySelected,
        isDayDisabled,
        onDayMouseEnter,
    ]);

    const onKeyDown: KeyboardEventHandler = useCallback(
        (event) => {
            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    needsToTakeFocus.current = true;
                    return setActiveDate(previousDay.range.from);
                case 'ArrowUp':
                    event.preventDefault();
                    needsToTakeFocus.current = true;
                    return setActiveDate(aboveDay.range.from);
                case 'ArrowRight':
                    event.preventDefault();
                    needsToTakeFocus.current = true;
                    return setActiveDate(nextDay.range.from);
                case 'ArrowDown':
                    event.preventDefault();
                    needsToTakeFocus.current = true;
                    return setActiveDate(belowDay.range.from);
            }
        },
        [aboveDay, belowDay, nextDay, previousDay, setActiveDate]
    );

    return (
        <table role="grid" ref={container} className={className}>
            <thead>
                <tr>
                    <th>{weekdayLabels[0]}</th>
                    <th>{weekdayLabels[1]}</th>
                    <th>{weekdayLabels[2]}</th>
                    <th>{weekdayLabels[3]}</th>
                    <th>{weekdayLabels[4]}</th>
                    <th>{weekdayLabels[5]}</th>
                    <th>{weekdayLabels[6]}</th>
                </tr>
            </thead>
            <tbody onKeyDown={onKeyDown}>
                {tableRows.map((row, index) => {
                    return (
                        <tr key={index}>
                            {row.map((renderedDay, index) => {
                                return (
                                    <td key={index} role="gridcell">
                                        {renderedDay}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
